import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardCheck, Loader2, Check, Pencil, X, RefreshCw, AlertTriangle,
  CheckCheck, Inbox,
} from 'lucide-react';
import {
  getAssignments, getPendingFeedback, approveFeedback, editFeedback,
  rejectFeedback, releaseFeedbackBatch,
} from '../../lib/api';
import { toast } from 'sonner';

/**
 * FlowGrade — the teacher review queue for AI coach feedback.
 *
 * "Nothing Reaches a Student Without You": on review-mode assignments every
 * AI feedback item waits here until the teacher approves, edits, or rejects
 * it. Coach-violation flags surface first. AI feedback only — teacher manual
 * feedback never enters this queue (it is teacher-authored by definition).
 */
export const FlowGradeView = () => {
  const [assignments, setAssignments] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending_review: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editNote, setEditNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getAssignments();
        const list = Array.isArray(data) ? data : data?.assignments || [];
        if (cancelled) return;
        setAssignments(list);
        if (list.length > 0) setSelectedId(list[0].id);
      } catch (error) {
        console.error('FlowGrade assignments load failed:', error);
        toast.error('Could not load your assignments.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const loadQueue = useCallback(async (assignmentId) => {
    if (!assignmentId) return;
    setQueueLoading(true);
    try {
      const data = await getPendingFeedback(assignmentId);
      setItems(data.items || []);
      setCounts(data.counts || { pending_review: 0, rejected: 0 });
    } catch (error) {
      console.error('FlowGrade queue load failed:', error);
      toast.error('Could not load the review queue.');
      setItems([]);
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(selectedId); }, [selectedId, loadQueue]);

  const act = async (item, fn, successMsg) => {
    setBusyId(item.ids[0]);
    try {
      await fn();
      toast.success(successMsg);
      await loadQueue(selectedId);
      return true;
    } catch (error) {
      console.error('FlowGrade action failed:', error);
      toast.error('That action did not go through. Try again.');
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const handleApprove = (item) =>
    act(item, () => approveFeedback(item.ids[0]), 'Released to the student.');

  const handleSaveEdit = async (item) => {
    const ok = await act(item, () => editFeedback(item.ids[0], editText, editNote), 'Edited and released.');
    // Only clear the editor on success — a failed release must not eat the
    // teacher's rewritten feedback.
    if (ok) { setEditingId(null); setEditText(''); setEditNote(''); }
  };

  const handleReject = async (item, regenerate) => {
    setBusyId(item.ids[0]);
    try {
      const result = await rejectFeedback(item.ids[0], regenerate);
      if (regenerate && result.regenerated) {
        toast.success('Rejected — a fresh version is back in your queue.');
      } else if (regenerate && result.retry) {
        toast.warning('Rejected. Regeneration is pending — use Regenerate to retry.');
      } else {
        toast.success('Rejected.');
      }
      await loadQueue(selectedId);
    } catch (error) {
      console.error('FlowGrade reject failed:', error);
      toast.error('That action did not go through. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveAll = async () => {
    if (!selectedId) return;
    setBusyId('all');
    try {
      const result = await releaseFeedbackBatch({ assignmentId: selectedId });
      toast.success(`Released ${result.released} item${result.released === 1 ? '' : 's'}.`);
      await loadQueue(selectedId);
    } catch (error) {
      console.error('FlowGrade release-all failed:', error);
      toast.error('Batch release did not go through. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.ids[0]);
    setEditText(item.feedback_text);
    setEditNote('');
  };

  // Quill drafts are HTML with entities (&nbsp; &quot; …). DOMParser gives
  // decoded plain text without executing anything — same safe pattern as
  // DraftSpace's stripHtml. Never inject draft HTML into the queue.
  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 p-6">
        <Loader2 size={16} className="animate-spin" /> Loading FlowGrade…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="flowgrade-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardCheck className="text-ai-accent" size={24} /> FlowGrade
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            AI feedback waits here until you release it. You have the final say.
          </p>
        </div>
        {items.length > 0 && (
          <button
            onClick={handleApproveAll}
            disabled={busyId !== null || counts.pending_review === 0}
            className="btn-teal flex items-center gap-2 disabled:opacity-50"
            data-testid="flowgrade-approve-all"
          >
            {busyId === 'all' ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />}
            Release all pending ({counts.pending_review})
          </button>
        )}
      </div>

      {assignments.length === 0 ? (
        <div className="card-surface p-8 text-center text-slate-400">
          <Inbox className="mx-auto mb-3 text-slate-500" size={32} />
          Create an assignment first — its AI feedback will land here for your review.
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-slate-400 text-sm" htmlFor="flowgrade-assignment">
            Assignment:
          </label>
          <select
            id="flowgrade-assignment"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2"
          >
            {assignments.map(a => (
              <option key={a.id} value={a.id}>{a.title}</option>
            ))}
          </select>
          <span className="text-slate-500 text-sm">
            {counts.pending_review} pending · {counts.rejected} rejected
          </span>
        </div>
      )}

      {queueLoading ? (
        <div className="flex items-center gap-2 text-slate-400 p-6">
          <Loader2 size={16} className="animate-spin" /> Loading the queue…
        </div>
      ) : items.length === 0 && assignments.length > 0 ? (
        <div className="card-surface p-8 text-center text-slate-400" data-testid="flowgrade-empty">
          <Check className="mx-auto mb-3 text-teal-flow" size={32} />
          All caught up — no feedback waiting for review.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => (
            <motion.div
              key={item.ids[0]}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card-surface p-4 ${item.coach_violation ? 'border-l-4 border-amber-500' : ''}`}
              data-testid="flowgrade-item"
            >
              {item.coach_violation && (
                <div className="flex items-center gap-2 text-amber-400 text-sm mb-3">
                  <AlertTriangle size={16} />
                  Flagged: this feedback may hand the student ready-made text. Review closely.
                </div>
              )}
              <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                <div>
                  <span className="text-white font-medium">{item.student_name}</span>
                  <span className="text-slate-500 text-xs ml-2">
                    {item.draft_word_count} words
                    {item.review_status === 'rejected' ? ' · rejected, pending regeneration' : ''}
                  </span>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {item.ccss_standards.map(std => (
                    <span key={std} className="badge-ccss">{std}</span>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-900/60 rounded-lg p-3 text-slate-400 text-sm max-h-48 overflow-y-auto">
                  <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Student draft</p>
                  {stripHtml(item.draft_content) || <em>Empty draft</em>}
                </div>
                <div className="bg-slate-900/60 rounded-lg p-3 text-sm max-h-48 overflow-y-auto">
                  <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Coach feedback</p>
                  {editingId === item.ids[0] ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={6}
                        className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg p-2"
                        aria-label="Edit coach feedback"
                      />
                      <input
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        placeholder="Optional note about your edit"
                        className="w-full bg-slate-800 border border-slate-700 text-slate-400 text-xs rounded-lg p-2"
                        aria-label="Edit note"
                      />
                    </div>
                  ) : (
                    <div className="text-slate-200 whitespace-pre-wrap">{item.feedback_text}</div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mt-3 flex-wrap">
                {editingId === item.ids[0] ? (
                  <>
                    <button
                      onClick={() => handleSaveEdit(item)}
                      disabled={busyId !== null || !editText.trim()}
                      className="btn-teal flex items-center gap-2 disabled:opacity-50"
                    >
                      <Check size={14} /> Save & release
                    </button>
                    <button
                      onClick={() => { setEditingId(null); setEditText(''); }}
                      className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {item.review_status !== 'rejected' && (
                      <button
                        onClick={() => handleApprove(item)}
                        disabled={busyId !== null}
                        className="btn-teal flex items-center gap-2 disabled:opacity-50"
                        data-testid="flowgrade-approve"
                      >
                        {busyId === item.ids[0] ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        Approve & release
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(item)}
                      disabled={busyId !== null}
                      className="btn-purple flex items-center gap-2 disabled:opacity-50"
                    >
                      <Pencil size={14} /> Edit
                    </button>
                    {item.review_status !== 'rejected' && (
                      <button
                        onClick={() => handleReject(item, false)}
                        disabled={busyId !== null}
                        className="px-3 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:border-red-500 hover:text-red-400 flex items-center gap-2 disabled:opacity-50"
                      >
                        <X size={14} /> Reject
                      </button>
                    )}
                    <button
                      onClick={() => handleReject(item, true)}
                      disabled={busyId !== null}
                      className="px-3 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:border-ai-accent hover:text-ai-accent flex items-center gap-2 disabled:opacity-50"
                      data-testid="flowgrade-regenerate"
                    >
                      <RefreshCw size={14} />
                      {item.review_status === 'rejected' ? 'Regenerate' : 'Reject & regenerate'}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FlowGradeView;
